import React from 'react';
import { Volume2, ChevronRight, Home, Compass, BookOpen, Landmark, Feather, MapPin } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface RobertsPart4SectionProps {
  onSelectPage?: (pageId: string) => void;
}

export const RobertsPart4Section: React.FC<RobertsPart4SectionProps> = ({ onSelectPage }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 text-[#2c221e] font-sans">
      {/* Top Breadcrumb / Page Header */}
      <header className="bg-[#f3ede0] p-6 sm:p-10 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          <div className="flex items-center gap-1.5 font-bold">
            {onSelectPage && (
              <button
                onClick={() => onSelectPage('hub')}
                className="hover:underline flex items-center gap-1 text-[#7a5b3a] cursor-pointer"
              >
                <Home className="w-3.5 h-3.5" />
                <span>The Roberts Family</span>
              </button>
            )}
            {onSelectPage && <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />}
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full uppercase tracking-wider text-[11px] border border-[#d8ceb9]">
              Part 4 of 5
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={speakCurrentPage}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
              title="Listen to Part 4: The Bike Ride to the Ballot at Starborough & T.E.L. Roberts"
            >
              <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
              <span>{isPlaying ? 'Listening to Part 4...' : 'Listen to Part 4'}</span>
            </button>
          </div>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight">
          THE BIKE RIDE TO THE BALLOT AT STARBOROUGH &amp; T.E.L. ROBERTS<sup className="font-bold text-[#7a5b3a] text-2xl hover:underline cursor-pointer"><a href="#fn-p4-1">1</a></sup>
        </h1>
        <p className="font-serif text-lg sm:text-xl text-[#7a5b3a] italic leading-relaxed">
          "The buoyancy of youth was in that ride, with a new world opening out."
        </p>
      </header>

      {/* Main Archival Article */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base leading-relaxed text-[#2c221e]">
        
        {/* Background */}
        <section className="space-y-6">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            Background
          </h2>

          <p>
            The Lands Settlement Act 1892 allowed the government to buy large properties and break them up for smaller farmers. Between 1899-1915 the Crown bought 22 large estates in Marlborough that were offered for ballot as 550 farms. Land ballots were competitive with men and women having the same opportunities.
          </p>

          <p>
            The 14,000 ha Starborough Estate (below) went to ballot on 16 March 1899 in Blenheim, offering first and second-class agricultural land, four small grazing runs, temporary grazing licences and sections for the township of Seddon - in all 47 farms and the four grazing runs for new farming settlers.
          </p>

          <p>
            Thomas Edward Lloyd (T.E.L.) or Tom Roberts was 26, the son of Robert and Sarah Roberts of Plas Isa in the Waikari Valley. <sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p4-2">2</a></sup>In his words he was "bent on sheep farming as far removed from horse work as possible, and not feeling quite equal to the grazing runs...I decided to select the larger of what were termed second class lands ranging up to 1000 acres. Without seeing the land at all I marked off 18 sections, any one of which would do me."
          </p>

          <p>
            He put in his application and then had to get to Blenheim. He bought a new bicycle for about 10 Pounds.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "On this glinting triumph of individual locomotion I set sail one early morning from Waikari to proceed through Culverden and Waiau, as at that time, and for many years later, there was no coastal road to Kaikoura. In the course of about three hours I found myself on the banks of the Mason Stream, which called for a decision as to whether I walk straight through or remove the boots. I decided on the latter, which meant a good deal of lost time, but against that my feet were dry and comfortable."
            </p>

            <p>
              "There were many streams to be crossed that day, of which I then had no knowledge, as the road was totally unknown to me beyond Waiau. With bicycle on shoulder it was a matter of minutes and the boots replaced. All that was quite good, but the water was tempting, and the day warm, which usually ended in a drink at each stream.
            </p>

            <p className="italic text-[#7a5b3a]">
              The old track crossing over the Wandle Stream by the confluence with the Mason River, and before the zig zag (October 2021). Much of the Inland Road has been realigned but there are still sections where it is exactly as Tom described.
            </p>

            <p>
              The Wandle Bush was passed, and there was quite useful timber there then, and so we trundled on. Over the Whale's Back and about 2 p.m. I arrived at the Conway changing station for the coach horses. I was done in as I was new to the bicycle, and the water drinking had found the weak spots. I walked into that bachelor-turnout fully convinced that Kaikoura would not see me that night. When I got to the door I was met by a man named Ted Barclay, whom I knew quite well, and he was mine host. It was no time till Ted had a good billy of tea, backed up with plenty of good merino mutton and bread, and oh, that tea was the best that T.E.L. ever drank in this world. We had a yarn, and I got up from the table a fit man, mounted my bicycle and arrived at Kaikoura "Just as the Sun Went Down" - the name of a popular song a little later."
            </p>
          </blockquote>

          <p>
            Tom stopped at the first hotel he saw at Kaikoura, the Adelphi. He was in bed and asleep very soon after dinner.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "At breakfast the following morning I discovered an old chum in the shape of one Charlie Maine, and he informed me it was too far to attempt Blenheim in one day. With that in mind I did not hurry as intended and the result was that I crossed the Lyell Bridge at 8.35 a.m.
            </p>
            <p>
              It was a delightfully sunny morning and for one seeing that sight for the first time it was indeed a feast for Canterbury eyes, The majesty of those mountains away to the left, with just a little snow on the tips, and with bush running right down to the seashore, left nothing to be desired let one's taste be what it may."
            </p>
          </blockquote>

          <p>
            He waded through the Hapuka. At that time Australian poet Henry Lawson was teaching school near Karetu Bay. His house later burnt down - Tom had tried to get a picture of it for an Australian paper but failed.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "The scenery as one took the curve into Karetu Bay, with the road running under arching ngaios, while tree ferns grew right to the water's edge, was of that particular type of beauty, beauty's best. The bush was alive with songsters, and with the sea rolling its long white streamers on the right hand, and with the bush clad hills rising sheer up from the roadside on the left, I rode along in what to me was a fairyland.
            </p>
            <p>
              Mile after mile almost to the Clarence River, it was one series of capes and bays, with continual expectancy as to what the next bend would reveal. The green cliffs, the blue sea glinting in the sunshine, the white, oh so white foam, dashing on the ragged rocks just below the roadway, and anyone, David Copperfield-like, asking for more, could scarcely be satisfied in this world.
            </p>
            <p>
              "In looking back over the years when so much bush was swept away for such poor purpose, one can only regret the folly of such destruction. Where so much that was beautiful is represented now by scrub and fern to our very shame. On that Sunday morning, past and future were forgotten in the delight of the movement, nor have I ever beheld a picture that thrilled me more intensely, and I have covered a wide range since then."
            </p>
          </blockquote>

          <p>
            The distance fell away with scarcely a pause, until he halted on the banks of a clear stream, for a snack and a drink - "but let it be remembered water drinking in the general way has ceased. I had had my lesson not to drink without eating at least a morsel of food."
          </p>

          <p>
            The road beyond Clarence was without shingle, and though the surface was relatively smooth, the going was not good but a stiff dry south-wester sprang up behind him which helped Tom continue in great style.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "After crossing the Ure there were signs of fresh survey, and I really thought I was on Starborough. Soon I arrived at Flaxbourne and learned that there was still some 13 miles to ride to touch Starborough. Along by Lake Elterwater, then Lake Grasmere and Starborough was in sight well on in the afternoon. In passing Star Hill I made a detour to the left to see what the country looked like out that way. I decided any of it as grazing land would do me at the prices asked. That was all the inspection I made and moved on with the idea of spending the night at the Awatere Hotel."
            </p>
          </blockquote>

          <p>
            The next river he had to pull off his pants to get through dry - but he noted there were no spectators, "so that the undressing and the dressing meant little in the way of embarrassment."
          </p>

          <p>
            At Awatere he rode over the Taylor Pass to arrive in Maxwell Road just as the lamps were being lighted. "No need to say that all I wanted was food and a bed, both were available. The bed and I were soon on the best of terms when oblivion called me away. I woke in the early hours to hear it pelting down rain and soon the house was awakened by travellers from Starborough who were compelled to get out in the night lest the river become uncrossable, and the ballot close at hand."
          </p>

          <p>
            Here Tom exchanged words with people who would become life friends - John Dollar, David Fleming, Enoch Orchard, Geo. Herd and others.<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p4-3">3</a></sup>
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "We were all from Canterbury and were strangers in a strange land, with one common objective, Starborough. We were soon a happy family, enjoying our jokes and little flirtations with the waitresses, for most of us were bachelors and away from home. There was a little lady barber down the street to whom we went just for the novelty of the thing."
            </p>
            <p>
              "We walked about, and talked about, and needless to say most of our conversation was about Starborough. None of us had ever faced a Land Board enquiry, and therefore had not the remotest idea of what faced us. It could be anything short of the Spanish Inquisition, deleting the rack, but nevertheless painful to meditation.
            </p>
            <p>
              The great day arrived and one after another name was called for the final analysis, while the rest of us awaited our turn they went in, and eventually came out, at least alive which was encouraging but still not very helpful. Each man kept his own counsel while we would fain have had some of the interrogations explained. Those were not the days of bulging bank accounts and most of us young men were trembling on the aye and nay, nor could we truthfully say the ayes had it. "
            </p>
          </blockquote>

          <p>
            At last his name was called, he took a last deep breath with his hand on the door knob.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "It was soon over, for no sooner that I was inside than the Hon. Charles Mills saluted me thus: "Mr Roberts, we are in the happy position of being able to inform you that you are the only applicant for at least two sections, and they are adjoining, will you take them both?"
            </p>
            <p>
              "Hold, hold!" says Commissioner Adams," we must examine this man."
            </p>
            <p>
              The examination was in my case, a pretty flimsy affair as I felt the ayes had it. What was my farming experience? Born to the land and the systems that obtained at that period. I was dismissed, taking only one section, and came out with a light heart."
            </p>
          </blockquote>

          <p>
            Tom was back on his bike by 1 p.m., spending that night at Wheeler's (Grasmere) so he would have an easy ride to Kaikoura the next day. He got his first slow puncture and was such a cycling novice he didn't realise he could screw the pump directly into the tyre valve, so he strolled quietly to Kaikoura and had it repaired. He was the subject of much interest as people there at a sheep sale were very keen to hear as much as they could about the results of the ballot.
          </p>

          <p>
            Tom wrote: "...those runs proved to be veritable gold mines, but it must be remembered that times had been very bad for the sheep men, pending the frozen meat trade development (from 1882). For some reason the wool season of 1899 and 1900 was better than for many years and gave Starborough settlers a great boost along."
          </p>

          {/* Image 231 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/231 Shearing for the wool boom.jpg"
              alt="Shearing for the wool boom"
              caption="The annual sheep returns showed that Tom had 517 sheep in April 1899, and 627 the next year. (Taken from &quot;Beyond the Hills of Hundalee&quot; by T.E.L. Roberts (1949))"
              filenameHint="231 Shearing for the wool boom.jpg"
            />
          </figure>

          {/* Image 232 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/232 Tetley Brook - now a vineyard.png"
              alt="Tetley Brook - now a vineyard"
              caption="Tetley Brook - now the name of a vineyard owned by Babich Wines- and in 1885 below"
              filenameHint="232 Tetley Brook - now a vineyard.png"
            />
          </figure>

          {/* Group of 4 Images: 233, 234, 235, 236 */}
          <div className="my-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ArchivalImageCard
                src="public/images/233 Tetley Brook 1885.jpg"
                alt="Tetley Brook 1885"
                caption="Tetley Brook 1885"
                filenameHint="233 Tetley Brook 1885.jpg"
              />
              <ArchivalImageCard
                src="public/images/234 Lonely Graver.jpg"
                alt="Lonely Grave"
                caption="Lonely Grave"
                filenameHint="234 Lonely Graver.jpg"
              />
              <ArchivalImageCard
                src="public/images/235 Headstone.jpg"
                alt="Headstone"
                caption="Headstone"
                filenameHint="235 Headstone.jpg"
              />
              <ArchivalImageCard
                src="public/images/236 Lonely grave.jpg"
                alt="Lonely grave"
                caption="Lonely grave"
                filenameHint="236 Lonely grave.jpg"
              />
            </div>
          </div>
        </section>

        {/* LONELY GRAVE POEM & HISTORY */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <div className="flex items-center gap-2 text-[#7a5b3a]">
            <Feather className="w-5 h-5" />
            <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              LONELY GRAVE
            </h2>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-sm sm:text-base leading-relaxed text-[#2c221e] italic space-y-3 whitespace-pre-line">
{`Beneath those rugged ranges grey; where silver tussocks wave
Beside the northern winding way, there is a circling fence today
That marks a lonely grave.
It is the bed of one who slept, out back and long ago
And we can vision those who wept around the little plot
And kept it fresh for flowers to grow.
But when they passed and all was still, the sheep that pastured came
And cattle wandered at their will above her sleeping on the hill
Where was no mark or name.
And o'er her grave the grasses spread until the mound was sealed
For months had passed and years had sped and nature's quilt was on her
bed, that nought of love revealed.
And then a stranger came and heard, and his kind heart went out,
The mound was freshened up and stirred and the fence so long deferred was
fitted round about.
He shaped a cross her name to grace, the name she briefly knew
And fixed it firmly in its place upon the Whales Back tussock face, 'neath
the heavens blue
And there it stands to mark the spot and we will mark the deed
And make it parcel with the plot that neither one may be forgot as flitting
years recede."

By T.E.L. Roberts 1921`}
          </div>

          <div className="space-y-4 pt-2">
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#2c221e]">
              Alice Cecilia Serecombe GEORGE<br />
              <span className="text-base text-[#7a5b3a] font-normal">Born June 1885 - Died April 1897</span>
            </h3>

            <p>
              Just before reaching the "The Doone" homestead on the bank of campbell Creek, on the left hand side of the road perched up on a little hill is a lonely grave. In 1887 during construction of the road from Waiau to Kaikoura, there was a contractor's camp situated on the Whalesback.
            </p>

            <p>
              Willie George was the name of the camp cook, he lived in a tent with his wife and daughter Alice - aged one year and ten months. Alice fell ill and before a doctor could be brought from Waiau she died.
            </p>

            <p>
              The Rev. W.R. Campbell, the Presbyterian minister known as "Apostle of the North" rode many miles over rough country to officiate at her burial on the roadside.
            </p>

            <p>
              After the camp moved on, the site was neglected until Captain A.W. Owles, of New Brighton, whose business frequently took him over the road from Waiau to Kaikoura made it his object to raise money to have the grave fenced. The Governors of Nelson College, on whose endowment land it is situated, gave the site. Mr Duncan Rutherford, who was then chairman of the Amuri County Council, had the fence erected.
            </p>

            <p>
              Mr Tom Derrett, the pioneer coachman on the Waiau to Kaikoura road, carted the timber free of charge. Today a stone marks the grave, enclosed by an iron railing with a post and wire fence surrounding the plot.
            </p>

            <p>
              Richard Roberts<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p4-4">4</a></sup> said the grave was always a stopover place on their annual trips to Ward and beyond as children. "We always had a particular sympathy..and perhaps empathy with a child alone in such a wild, lonely place. I suppose it was that being younger, and smaller, in a place larger and seemingly towered over us that it impressed itself on us."
            </p>
          </div>

          {/* Image 237 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/237 The road to the Loney Grave.jpg"
              alt="The road to the Lonely Grave"
              caption="The road to the Lonely Grave"
              filenameHint="237 The road to the Loney Grave.jpg"
            />
          </figure>
        </section>

        {/* T.E.L. Roberts Letter to Brother Ted */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl font-bold text-[#2c221e]">
            T.E.L. Roberts wrote to his brother Ted about some reaction to his published book.
          </h2>
          <p className="font-mono text-sm font-bold text-[#7a5b3a]">
            9.10.49
          </p>

          {/* Image 237A */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/237A Excerpt of letter.jpg"
              alt="Excerpt of letter"
              caption="Excerpt of letter from T.E.L. Roberts"
              filenameHint="237A Excerpt of letter.jpg"
            />
          </figure>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              C. Caverhill says it will be one of his most treasured possessions, and that it will sell like hot cakes because of the verse in it. Tom Mills says it is excellent stuff and the verse of the best, and much appreciated by him. Tom makes special mention of my tribute to Pukekura Park, which he says pleases him. I feel that I have not failed and so far it would rather seem that I have exceeded expectations.
            </p>
            <p>
              Probably I got the best phrase from Beryl, who is a deep reader. She got the book on Wednesday evening before Scargill show, on the Sat, and as she had heavy entries here time was precious. She burnt her potatoes she boiled over her milk and did many other things, but she read the book, and then went to the show and won her trophy. The philosophy is her fancy and she says I will get much favourable comment - I am filing the letters. You need not go to much trouble to sell yours as if I print anymore they could double their money.
            </p>
            <p className="font-bold">
              The best<br />
              T.E.L
            </p>
            <p className="text-sm italic text-[#5c4328]">
              The University of California have thanked me by letter for 'Beyond the Hills of Hundalee.'
            </p>
          </blockquote>

          {/* Image 238 & 239 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/238 Book.jpg"
              alt="Beyond the Hills of Hundalee Book"
              caption="Beyond the Hills of Hundalee by T.E.L. Roberts"
              filenameHint="238 Book.jpg"
            />
            <ArchivalImageCard
              src="public/images/239 Rimu and Rata.jpg"
              alt="Rimu and Rata"
              caption="Rimu and Rata"
              filenameHint="239 Rimu and Rata.jpg"
            />
          </div>
        </section>

        {/* FOOTNOTES BLOCK */}
        <section className="pt-8 border-t-2 border-[#d8ceb9] space-y-3 text-xs text-[#5c4328]">
          <div className="flex items-center gap-1.5 font-serif font-bold text-sm text-[#7a5b3a] mb-2">
            <Landmark className="w-4 h-4" />
            <span>Footnotes &amp; Archival Citations (Part 4)</span>
          </div>

          <ol className="space-y-1.5 pl-5 list-decimal">
            <li id="fn-p4-1">
              TEL also called Tom - he left a record of his early life so that has provided a rich look at a young life in the early European settlement in the South Island
            </li>
            <li id="fn-p4-2">
              Robert Morris Morris came to NZ in 1862, Sarah Baxter came in 1864. They married 24 December 1866.
            </li>
            <li id="fn-p4-3">
              The Dollar and Orchard families still farm on the original properties (at 2021)
            </li>
            <li id="fn-p4-4">
              Grandson of Robert Morris Roberts, son of Edward T. Roberts
            </li>
          </ol>
        </section>
      </article>

      {/* Footer Navigation */}
      <footer className="flex flex-wrap items-center justify-between gap-4 p-6 bg-[#f3ede0] rounded-2xl border border-[#d8ceb9]">
        <div className="flex items-center gap-2">
          {onSelectPage && (
            <button
              onClick={() => onSelectPage('part3')}
              className="flex items-center gap-2 px-4 py-2 bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#2c221e] font-serif font-bold text-xs rounded-xl transition cursor-pointer"
            >
              <span>&larr; Previous: Part 3 (Ted Roberts)</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onSelectPage && (
            <button
              onClick={() => onSelectPage('part5')}
              className="flex items-center gap-2 px-4 py-2 bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-xs rounded-xl transition cursor-pointer"
            >
              <span>Next: Part 5 (Upcoming) &rarr;</span>
            </button>
          )}
        </div>
      </footer>
    </div>
  );
};
